// src/utils/analyticsRows.js
// Row rules for the pivoted analytics table (rows = metrics, columns = tranches).
//
// The backend returns one metric list (get_analytics_df in
// trade_server_utils.py) and every surface shows all of it: the desktop panel,
// the mobile table and the comparison grids render whatever rows came back,
// in the analysts' reading order (orderAnalyticsRows below).
// Two things still vary by row. Some metrics hold text rather than numbers, so
// a numeric formatter would blank them out. And some metrics come from the
// position (Clearwater book and market data), so for a tranche we have never
// bought they are structurally empty — that tranche is a new CUSIP, its column
// is marked, and the position rows come out of the table.

export const METRIC_FIELD = 'Metric';

// The row that says whether we hold the tranche at all.
export const BOOK_VALUE_METRIC = 'Book Value';

// Rows that only exist for a security we hold.
export const POSITION_METRICS = Object.freeze([
  'Book Value',
  'Book Price',
  'Book Yield',
  'Market Value',
  'Market Price',
  'Market Yield',
  'Projected Market Yield',
  'Projected Market Spread',
  'Projected Market DM',
  'YTD Annualized Return',
  'ITD Annualized Return',
]);

// Rows whose values are text (a date window, a price source) rather than
// numbers. Grid formatters otherwise render any non-number as missing ('--').
export const TEXT_METRICS = Object.freeze([
  'Principal Window',
  'P&I Window',
  'Price Source',
  'Benchmark Date',
]);

const POSITION_METRIC_NAMES = new Set(POSITION_METRICS);
const TEXT_METRIC_NAMES = new Set(TEXT_METRICS);

export const isPositionMetric = (metric) => POSITION_METRIC_NAMES.has(metric);

export const isTextMetric = (metric) => TEXT_METRIC_NAMES.has(metric);

// Shown above the table when the position rows have been dropped.
export const NEW_CUSIP_NOTICE = 'New CUSIP — no position, so book and market metrics are hidden.';

// Marker appended to (or shown beside) the header of a new tranche's column.
export const NEW_TRANCHE_LABEL = 'New';

// No book value — no row, no number, or zero — means we hold none of the
// tranche. A non-numeric string ('Error') counts as none for the same reason.
export const isMissingBookValue = (value) => {
  if (value === null || value === undefined || value === '') return true;
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(numeric) || numeric === 0;
};

const isPivot = (rows, metricField) =>
  Array.isArray(rows) && rows.some((row) => row && row[metricField] !== undefined);

// Every column but the metric name, taken from the first row. Callers that show
// a subset (a tranche selector, one run of a comparison) pass their own list.
const defaultColumns = (rows, metricField) => {
  const first = (Array.isArray(rows) ? rows : []).find((row) => row && typeof row === 'object');
  return first ? Object.keys(first).filter((key) => key !== metricField) : [];
};

// The displayed columns that hold nothing. Rows that are not a pivot, and data
// with no book-value row at all, judge nothing as new: without that row there
// is no evidence either way, and hiding metrics on a guess is worse than
// showing empty ones.
export const getNewTrancheColumns = (rows, { metricField = METRIC_FIELD, columns } = {}) => {
  if (!isPivot(rows, metricField)) return new Set();
  const bookValueRow = rows.find((row) => row && row[metricField] === BOOK_VALUE_METRIC);
  if (!bookValueRow) return new Set();
  const displayed = columns || defaultColumns(rows, metricField);
  return new Set(displayed.filter((column) => isMissingBookValue(bookValueRow[column])));
};

// Whether every displayed tranche is a new CUSIP.
export const hasOnlyNewTranches = (rows, { metricField = METRIC_FIELD, columns } = {}) => {
  const displayed = columns || defaultColumns(rows, metricField);
  if (displayed.length === 0) return false;
  return getNewTrancheColumns(rows, { metricField, columns: displayed }).size === displayed.length;
};

// The rows to show. A pivot cannot hide a row for one column only, so the
// position rows survive as long as one displayed tranche is held — the held
// tranches fill them in and the new ones read empty.
export const filterAnalyticsRows = (rows, { metricField = METRIC_FIELD, columns } = {}) => {
  if (!Array.isArray(rows)) return [];
  if (!hasOnlyNewTranches(rows, { metricField, columns })) return rows;
  return rows.filter((row) => !isPositionMetric(row?.[metricField]));
};

// The order analysts read a tranche in: the price and its source, the benchmark
// date, yield and spreads, risk, then the cashflow windows and losses. Rows not
// listed follow in the order the backend sent them; listed rows that did not
// come back are simply absent.
export const METRIC_ORDER = Object.freeze([
  'Price Used',
  'Price Source',
  'Benchmark Date',
  'Yield',
  'Spread vs Treasury (bp)',
  'Spread vs SOFR (bp)',
  'DM',
  'Modified Duration',
  'Modified Convexity',
  'WAL',
  'Principal Window',
  'P&I Window',
  'Principal Writedown',
  'Collateral Loss %',
  'Collateral Loss % incl. History',
]);

const METRIC_RANK = new Map(METRIC_ORDER.map((metric, index) => [metric, index]));

const metricRank = (metric) =>
  (METRIC_RANK.has(metric) ? METRIC_RANK.get(metric) : METRIC_ORDER.length);

// The rows in METRIC_ORDER. A new array for a pivot (Array.prototype.sort is
// stable, so unlisted rows keep their backend order); anything that is not a
// pivot comes back untouched.
export const orderAnalyticsRows = (rows, { metricField = METRIC_FIELD } = {}) => {
  if (!Array.isArray(rows)) return [];
  if (!isPivot(rows, metricField)) return rows;
  return [...rows].sort((a, b) => metricRank(a?.[metricField]) - metricRank(b?.[metricField]));
};
